/** Quantidade de vias e rótulos para receituários especiais (controle, antimicrobiano, etc.). */

import { resolveEffectivePrescriptionType } from './prescriptionTypes';

export function resolvePrescriptionViaCount(
  prescriptionType?: string | null,
  items?: Array<{ is_controlled?: boolean; is_antimicrobial?: boolean }> | null,
): number {
  const type = resolveEffectivePrescriptionType(prescriptionType, items);
  if (type === 'controlled_yellow') return 3;
  if (['controlled_blue', 'special_white', 'special_control', 'antimicrobial', 'dental'].includes(type)) {
    return 2;
  }
  return 1;
}

export function getPrescriptionViaLabels(viaCount: number): string[] {
  if (viaCount >= 3) {
    return ['1ª Via — Farmácia', '2ª Via — Notificação', '3ª Via — Paciente'];
  }
  if (viaCount === 2) {
    return ['1ª Via — Farmácia (retenção)', '2ª Via — Paciente'];
  }
  return [];
}

export function effectivePrescriptionViaCount(
  prescription: { via_count?: number | null; prescription_type?: string | null; items?: any[] | null },
): number {
  const items = Array.isArray(prescription?.items) ? prescription.items : [];
  const resolved = resolvePrescriptionViaCount(prescription?.prescription_type, items);
  // Com itens estruturados, o tipo/vias vêm dos medicamentos (via_count antigo pode estar desatualizado).
  if (items.length > 0) return resolved;

  const stored = Number(prescription?.via_count);
  if (!Number.isFinite(stored) || stored < 1) return resolved;
  return Math.max(stored, resolved);
}

/** Medicamento que a farmácia retém — é o que obriga a segunda via. */
export function itemRequiresRetention(
  item?: { is_controlled?: boolean; is_antimicrobial?: boolean } | null,
): boolean {
  return Boolean(item?.is_controlled || item?.is_antimicrobial);
}

export type PrescriptionViaBlock = {
  items: any[];
  viaCount: number;
  prescriptionType: string;
};

/**
 * Divide a receita nos blocos que viram documentos distintos na impressão.
 *
 * Um único antimicrobiano obrigava a receita inteira a sair em duas vias, então
 * o paciente levava o paracetamol duplicado junto. Separando, os medicamentos
 * comuns saem em via única e só o bloco de retenção é duplicado — menos papel e
 * menos tinta, sem perder a via que a farmácia precisa reter.
 *
 * Texto livre não tem itens para classificar, então continua como bloco único.
 */
export function splitPrescriptionByViaRequirement(prescription: {
  prescription_type?: string | null;
  via_count?: number | null;
  format?: string | null;
  items?: any[] | null;
}): PrescriptionViaBlock[] {
  const items = Array.isArray(prescription?.items) ? prescription.items : [];
  const singleBlock: PrescriptionViaBlock[] = [{
    items,
    viaCount: effectivePrescriptionViaCount(prescription),
    prescriptionType: resolveEffectivePrescriptionType(prescription?.prescription_type, items),
  }];

  if (String(prescription?.format || '') === 'free_text') return singleBlock;

  const retained = items.filter(itemRequiresRetention);
  const plain = items.filter((item) => !itemRequiresRetention(item));
  if (!retained.length || !plain.length) return singleBlock;

  const retainedType = resolveEffectivePrescriptionType(prescription?.prescription_type, retained);
  return [
    { items: plain, viaCount: 1, prescriptionType: 'simple' },
    {
      items: retained,
      viaCount: effectivePrescriptionViaCount({
        via_count: prescription?.via_count,
        prescription_type: retainedType,
        items: retained,
      }),
      prescriptionType: retainedType,
    },
  ];
}
